import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Button,
  Table,
  Modal,
  Row,
  Col,
  Container,
  Spinner,
  InputGroup,
  Card,
} from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaPlus,
  FaSearch,
  FaTimes,
  FaClock,
  FaMinus,
} from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const GOOGLE_MAPS_API_KEY = "AIzaSyApzcbstXMYo6vM24Hoxp6o1CQcyjLY_SM";
const API_BASE_URL =
  "https://api.dremerz.net/api/DZ_8922_PayrollProduct_PayrollAddLocations_PayrollLocationDetails";

const AddLocation = () => {
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState({
    page: true,
    map: false,
    submitting: false,
    address: false,
    suggestions: false,
  });
  const [showMapModal, setShowMapModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [map, setMap] = useState(null);
  const markerRef = useRef(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);

  // Refs
  const mapRef = useRef(null);
  const googleMapScriptRef = useRef(null);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const companyKey = sessionStorage.getItem("companyKey");

  // Form state with shifts
  // In useState for formData (initial)
  const [formData, setFormData] = useState({
    locationName: "",
    latitude: "",
    longitude: "",
    radius: 100,
    address: "",
    shifts: [
      {
        shiftName: "Shift 1",
        workingHours: 8,
        startTime: "09:00", // 24h
        endTime: "17:00", // 24h
        // breakTime: "60", // in mins
      },
    ],
  });

  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);

  const formatWorkingHours = (shifts) => {
    const parsedShifts = parseShifts(shifts);
    if (parsedShifts.length === 0) return "No shifts";

    return parsedShifts.map((shift, i) => (
      <div key={i}>
        {shift.shiftName}: {shift.workingHours} hours
      </div>
    ));
  };

  // ---- 24h-only helpers ----
  const to24h = (timeStr) => {
    // Accepts 'HH:mm' or 'h[:mm] AM/PM' and returns 'HH:mm'
    if (!timeStr) return "";
    const s = String(timeStr).trim();

    // e.g., "9 AM", "9:30 pm"
    const ampm = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (ampm) {
      let h = parseInt(ampm[1], 10);
      const m = (ampm[2] ?? "00").padStart(2, "0");
      const p = ampm[3].toUpperCase();
      if (p === "PM" && h !== 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
      return `${h.toString().padStart(2, "0")}:${m}`;
    }

    // e.g., "09:05" (already 24h)
    const hm = s.match(/^(\d{1,2}):(\d{2})$/);
    if (hm) {
      let h = Math.max(0, Math.min(23, parseInt(hm[1], 10)));
      const m = hm[2].padStart(2, "0");
      return `${h.toString().padStart(2, "0")}:${m}`;
    }

    // fallback: try to parse hour only
    const hOnly = s.match(/^(\d{1,2})$/);
    if (hOnly) {
      const h = Math.max(0, Math.min(23, parseInt(hOnly[1], 10)));
      return `${h.toString().padStart(2, "0")}:00`;
    }
    return "";
  };

  const addHoursToTime24 = (startTime24, hoursFloat) => {
    if (!startTime24 || !Number.isFinite(hoursFloat)) return "";
    const [hStr, mStr = "00"] = startTime24.split(":");
    const startMin = (parseInt(hStr, 10) || 0) * 60 + (parseInt(mStr, 10) || 0);
    const delta = Math.round(hoursFloat * 60);
    const endMin = (((startMin + delta) % (24 * 60)) + 24 * 60) % (24 * 60); // wrap safely
    const endH = Math.floor(endMin / 60);
    const endM = endMin % 60;
    return `${endH.toString().padStart(2, "0")}:${endM
      .toString()
      .padStart(2, "0")}`;
  };

  const hoursBetween24 = (startTime24, endTime24) => {
    // positive diff in hours (wrap midnight)
    if (!startTime24 || !endTime24) return null;
    const [sh, sm] = startTime24.split(":").map((n) => parseInt(n, 10));
    const [eh, em] = endTime24.split(":").map((n) => parseInt(n, 10));
    let diff = eh * 60 + em - (sh * 60 + sm);
    if (diff < 0) diff += 24 * 60;
    return diff / 60;
  };

  const parseShifts = (shifts) => {
    if (!shifts) return [];
    if (Array.isArray(shifts)) return shifts;
    if (typeof shifts === "string") {
      try {
        return JSON.parse(shifts);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const convertTo24Hour = (time, period) => {
    if (!time) return "";

    const [hours, minutes] = time.split(":");
    let hour24 = parseInt(hours);

    if (period === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (period === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    return `${hour24.toString().padStart(2, "0")}:${minutes}`;
  };

  const convertTo12Hour = (time24) => {
    if (!time24) return { time: "", period: "AM" };

    const [hours, minutes] = time24.split(":");
    let hour12 = parseInt(hours);
    let period = "AM";

    if (hour12 === 0) {
      hour12 = 12;
    } else if (hour12 === 12) {
      period = "PM";
    } else if (hour12 > 12) {
      hour12 -= 12;
      period = "PM";
    }

    return {
      time: `${hour12.toString().padStart(2, "0")}:${minutes}`,
      period: period,
    };
  };

  const initializePlacesService = () => {
    if (window.google && window.google.maps) {
      setAutocompleteService(
        new window.google.maps.places.AutocompleteService()
      );
      setPlacesService(
        new window.google.maps.places.PlacesService(
          document.createElement("div")
        )
      );
    }
  };

  const getAddressSuggestions = (input) => {
    if (!autocompleteService || !input.trim()) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading((prev) => ({ ...prev, suggestions: true }));

    autocompleteService.getPlacePredictions(
      {
        input: input,
        types: ["address"],
        componentRestrictions: { country: "in" },
      },
      (predictions, status) => {
        setLoading((prev) => ({ ...prev, suggestions: false }));

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setAddressSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  };

  const handleAddressInputChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      address: value,
    }));

    if (!value.trim()) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    clearTimeout(window.suggestionsTimeout);
    window.suggestionsTimeout = setTimeout(() => {
      getAddressSuggestions(value);
    }, 300);
  };

  const selectAddressSuggestion = (placeId, description) => {
    if (!placesService) return;

    setLoading((prev) => ({ ...prev, address: true }));
    setShowSuggestions(false);

    placesService.getDetails(
      {
        placeId: placeId,
        fields: ["geometry", "formatted_address", "name"],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          setFormData((prev) => ({
            ...prev,
            address: place.formatted_address,
            latitude: lat,
            longitude: lng,
          }));

          if (map) {
            const location = new window.google.maps.LatLng(lat, lng);
            map.setCenter(location);
            map.setZoom(15);
            updateMarker(location, map);
          }
        }
        setLoading((prev) => ({ ...prev, address: false }));
      }
    );
  };

  const searchAddress = () => {
    if (!window.google || !window.google.maps) {
      showMyAlert({
        type: "danger",
        title: "Error",
        message: "Google Maps not loaded",
        duration: 2500,
      });
      return;
    }

    const address = formData.address.trim();
    if (!address) {
      showMyAlert({
        type: "warning",
        title: "Validation Error",
        message: "Please enter an address to search",
        duration: 2500,
      });
      return;
    }

    setLoading((prev) => ({ ...prev, address: true }));
    setShowSuggestions(false);

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: address }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        setFormData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          address: results[0].formatted_address,
        }));

        if (map) {
          map.setCenter(location);
          map.setZoom(15);
          updateMarker(location, map);
        }
      } else {
        showMyAlert({
          type: "danger",
          title: "Error",
          message: "Address not found. Please try a different address.",
          duration: 2500,
        });
        console.warn("Geocode failed:", status);
      }
      setLoading((prev) => ({ ...prev, address: false }));
    });
  };

  const fetchAddress = async (lat, lng) => {
    try {
      setLoading((prev) => ({ ...prev, address: true }));

      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat: parseFloat(lat), lng: parseFloat(lng) };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          const address = results[0].formatted_address;
          setFormData((prev) => ({
            ...prev,
            address: address,
          }));
        } else {
          console.warn("Geocoder failed:", status);
          setFormData((prev) => ({
            ...prev,
            address: "Address not found",
          }));
        }
        setLoading((prev) => ({ ...prev, address: false }));
      });
    } catch (error) {
      console.error("Error fetching address:", error);
      setFormData((prev) => ({
        ...prev,
        address: "Error fetching address",
      }));
      setLoading((prev) => ({ ...prev, address: false }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const userMobile = sessionStorage.getItem("userMobile");

        if (!userMobile || userMobile.trim() === "") {
          console.warn("No user mobile found - redirecting to login");
          navigate(`/`, { replace: true });
          return;
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        navigate(`/`, { replace: true });
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredLocations(locations);
    } else {
      const term = searchTerm.toLowerCase();

      const filtered = locations.filter((location) => {
        const allFields = [
          location.locationName,
          location.address,
          location.latitude,
          location.longitude,
          location.radius,
          location.locationId,
        ];

        let shiftsArray = [];
        if (location.shifts) {
          try {
            const parsedShifts = JSON.parse(location.shifts);
            shiftsArray = parsedShifts.flatMap((shift) => [
              shift.shiftName,
              String(shift.workingHours),
              shift.startTime,
              shift.endTime,
            ]);
          } catch (e) {
            console.warn("Invalid shifts JSON", e);
          }
        }

        const combinedFields = [...allFields, ...shiftsArray]
          .filter(Boolean)
          .map(String)
          .map((s) => s.toLowerCase());
        return combinedFields.some((field) => field.includes(term));
      });

      setFilteredLocations(filtered);
    }
  }, [searchTerm, locations]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const addShift = () => {
    const newShiftNumber = formData.shifts.length + 1;
    const newShift = {
      shiftName: `Shift ${newShiftNumber}`,
      workingHours: 8,
      startTime: "09:00",
      startPeriod: "AM",
      endTime: "05:00",
      endPeriod: "PM",
      // breakTime: "60",
    };

    setFormData((prev) => ({
      ...prev,
      shifts: [...prev.shifts, newShift],
    }));
  };

  const removeShift = (index) => {
    if (formData.shifts.length > 1) {
      setFormData((prev) => ({
        ...prev,
        shifts: prev.shifts.filter((_, i) => i !== index),
      }));
    }
  };

  const handleShiftChange = (index, field, value) => {
    setFormData((prev) => {
      const newShifts = [...prev.shifts];
      const shift = { ...newShifts[index] };

      if (field === "startTime") {
        shift.startTime = to24h(value);
      } else if (field === "workingHours") {
        shift.workingHours = parseFloat(value) || 0;
      } else {
        shift[field] = value;
      }

      // Recompute endTime whenever startTime or workingHours changes
      if (["startTime", "workingHours"].includes(field)) {
        shift.endTime = addHoursToTime24(shift.startTime, shift.workingHours);
      }

      newShifts[index] = shift;
      return { ...prev, shifts: newShifts };
    });
  };

  const exportToExcel = () => {
    const exportData = filteredLocations.map((location, index) => {
      const parsedShifts = parseShifts(location.shifts);
      const shiftsText =
        parsedShifts.length > 0
          ? parsedShifts
            .map((shift) => `${shift.shiftName}: ${formatDisplayTime(shift)}`)
            .join("; ")
          : "No shifts";

      return {
        "S.No": index + 1,
        "Location Name": location.locationName,
        Address: location.address || "No address",
        Coordinates: `${location.latitude}, ${location.longitude}`,
        "Radius (m)": location.radius,
        Shifts: shiftsText,
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Locations");
    XLSX.writeFile(
      wb,
      `locations_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Location Management Report", 14, 22);
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 32);
    doc.text(`Total Locations: ${filteredLocations.length}`, 14, 42);

    const tableData = filteredLocations.map((location, index) => {
      const parsedShifts = parseShifts(location.shifts);
      const shiftsText =
        parsedShifts.length > 0
          ? parsedShifts
            .map((shift) => `${shift.shiftName}: ${formatDisplayTime(shift)}`)
            .join("\n")
          : "No shifts";

      return [
        index + 1,
        location.locationName,
        location.address || "No address",
        `${location.latitude}, ${location.longitude}`,
        `${location.radius}m`,
        shiftsText,
      ];
    });

    autoTable(doc, {
      head: [["#", "Name", "Address", "Coordinates", "Radius", "Shifts"]],
      body: tableData,
      startY: 50,
      styles: { fontSize: 8 },
      columnStyles: {
        2: { cellWidth: 40 },
        5: { cellWidth: 30 },
      },
    });

    doc.save(`locations_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  useEffect(() => {
    // if (showMapModal) {
    setLoading((prev) => ({ ...prev, map: true }));


    if (window.google && window.google.maps) {
      initializeMap();
      initializePlacesService();
      return;
    }

    if (document.querySelector(`script[src*="maps.googleapis.com"]`)) {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogle);
          initializeMap();
          initializePlacesService();
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.maps) {
        initializeMap();
        initializePlacesService();
      } else {
        showMyAlert({
          type: "danger",
          title: "Error",
          message: "Failed to load Google Maps",
          duration: 2500,
        });
        setLoading((prev) => ({ ...prev, map: false }));
      }
    };
    script.onerror = () => {
      showMyAlert({
        type: "danger",
        title: "Error",
        message: "Failed to load Google Maps script",
        duration: 2500,
      });
      setLoading((prev) => ({ ...prev, map: false }));
    };

    googleMapScriptRef.current = script;
    document.body.appendChild(script);

    return () => {
      if (googleMapScriptRef.current) {
        document.body.removeChild(googleMapScriptRef.current);
      }
    };
  }, [showMapModal]);

  const initializeMap = () => {
    if (!mapRef.current) {
      // showMyAlert({
      //   type: "danger",
      //   title: "Error",
      //   message: "Map container not found",
      //   duration: 2500,
      // });
      setLoading((prev) => ({ ...prev, map: false }));
      return;
    }

    try {
      const mapOptions = {
        center: selectedLocation
          ? {
            lat: parseFloat(selectedLocation.latitude),
            lng: parseFloat(selectedLocation.longitude),
          }
          : { lat: 0, lng: 0 },
        zoom: 13,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      };

      const mapInstance = new window.google.maps.Map(
        mapRef.current,
        mapOptions
      );
      setMap(mapInstance);

      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }

      mapInstance.addListener("click", (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        setFormData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));

        updateMarker(e.latLng, mapInstance);
        fetchAddress(lat, lng);
        setShowSuggestions(false);
      });

      if (selectedLocation) {
        const position = new window.google.maps.LatLng(
          parseFloat(selectedLocation.latitude),
          parseFloat(selectedLocation.longitude)
        );
        updateMarker(position, mapInstance);
        mapInstance.setCenter(position);
      } else {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              mapInstance.setCenter(pos);
            },
            () => {
              mapInstance.setCenter({ lat: 0, lng: 0 });
            }
          );
        }
      }

      setLoading((prev) => ({ ...prev, map: false }));
    } catch (err) {
      showMyAlert({
        type: "danger",
        title: "Error",
        message: "Failed to initialize Google Maps",
        duration: 2500,
      });
      setLoading((prev) => ({ ...prev, map: false }));
      console.error("Google Maps initialization error:", err);
    }
  };

  const updateMarker = (position, mapInstance) => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    const newMarker = new window.google.maps.Marker({
      position,
      map: mapInstance,
      draggable: true,
      title: "Selected Location",
    });

    newMarker.addListener("dragend", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setFormData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));

      fetchAddress(lat, lng);
      setShowSuggestions(false);
    });

    markerRef.current = newMarker;
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading((prev) => ({ ...prev, page: true }));
        const response = await axios.get(API_BASE_URL);
        const filteredLocations = response.data.filter((l) => l.companyKey === companyKey);
        setLocations(filteredLocations);
        setLoading((prev) => ({ ...prev, page: false }));
      } catch (err) {
        showMyAlert({
          type: "danger",
          title: "Error",
          message:
            "Failed to fetch locations: " +
            (err.response?.data?.message || err.message),
          duration: 2500,
        });
      } finally {
        setLoading((prev) => ({ ...prev, page: false }));
      }
    };

    fetchLocations();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading((prev) => ({ ...prev, submitting: true }));

      const shiftsForSave = formData.shifts.map((s) => ({
        shiftName: s.shiftName,
        workingHours: Number(s.workingHours) || 0,
        startTime: to24h(s.startTime),
        endTime: to24h(s.endTime),
        // breakTime: Number(s.breakTime) || 0,
      }));

      const payload = {
        locationName: formData.locationName,
        latitude: formData.latitude,
        longitude: formData.longitude,
        radius: formData.radius,
        address: formData.address,
        shifts: shiftsForSave, // 24h-only
      };
      const duplicate = locations.some(
        (loc) =>
          loc.locationName.trim().toLowerCase() ===
          payload.locationName.toLowerCase()
      );

      if (!selectedLocation && duplicate) {
        showMyAlert({
          type: "Already Exists", // "success" | "danger" | "warning" | "info"
          title: "No Data",
          message: ` Location "${formData.locationName}" already exists!`,
          duration: 2500, // auto-close in 2.5s
        });
        setLoading((prev) => ({ ...prev, submitting: false }));
        return;
      }

      let response;
      if (selectedLocation) {
        const oldData = {
          locationName: selectedLocation.locationName,
          latitude: selectedLocation.latitude,
          longitude: selectedLocation.longitude,
          radius: selectedLocation.radius,
          address: selectedLocation.address,
          shifts: selectedLocation.shifts,
        };

        response = await axios.put(
          `${API_BASE_URL}/${selectedLocation.id}`,
          payload
        );

        doAuditLog("Update", "Add Location", selectedLocation, payload);
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "Updated",
          message: `Location "${formData.locationName}" updated successfully!`,
          duration: 2500, // auto-close in 2.5s
        });
      } else {
        response = await axios.post(API_BASE_URL, {
          ...payload,
          companyKey: companyKey,
        });
        doAuditLog("Create", "Add Location", "", payload);
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "Added",
          message: `Location "${formData.locationName}" added successfully!`,
          duration: 2500, // auto-close in 2.5s
        });
      }

      const locationsResponse = await axios.get(API_BASE_URL);
      const filterLocation = locationsResponse.data.filter((l) => l.companyKey === companyKey);
      setLocations(filterLocation);

      resetForm();
    } catch (err) {
      showMyAlert({
        type: "danger",
        title: "Error",
        message:
          "Failed to save location: " +
          (err.response?.data?.message || err.message),
        duration: 2500,
      });
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      locationName: "",
      latitude: "",
      longitude: "",
      radius: 100,
      address: "",
      shifts: [
        {
          shiftName: "Shift 1",
          workingHours: 8,
          startTime: "09:00",
          startPeriod: "AM",
          endTime: "05:00",
          endPeriod: "PM",
          // breakTime: "60",
        },
      ],
    });
    setSelectedLocation(null);
    setShowMapModal(false);
    setShowSuggestions(false);
    setAddressSuggestions([]);

    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
  };

  const handleEdit = (location) => {
    setSelectedLocation(location);
    const parsedShifts = parseShifts(location.shifts);

    const shiftsForState =
      parsedShifts.length > 0
        ? parsedShifts.map((s) => {
          const start24 = to24h(s.startTime);
          const end24 = to24h(s.endTime);
          // Prefer stored workingHours; if missing, infer from start/end
          const wh = Number(s.workingHours);
          const inferred = hoursBetween24(start24, end24);
          return {
            shiftName: s.shiftName || "Shift",
            workingHours: Number.isFinite(wh) ? wh : inferred ?? 8,
            startTime: start24,
            endTime: end24,
            // breakTime: s.breakTime || 0,
          };
        })
        : [
          {
            shiftName: "Shift 1",
            workingHours: 8,
            startTime: "09:00",
            endTime: "17:00",
            // breakTime: 60,
          },
        ];

    setFormData({
      locationName: location.locationName,
      latitude: location.latitude,
      longitude: location.longitude,
      radius: location.radius,
      address: location.address || "",
      shifts: shiftsForState,
    });
    setShowMapModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, submitting: true }));

      const oldData = {
        locationName: selectedLocation.locationName,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        radius: selectedLocation.radius,
        address: selectedLocation.address,
        shifts: selectedLocation.shifts,
      };

      await axios.delete(`${API_BASE_URL}/${selectedLocation.id}`);

      doAuditLog("Delete", "Add Location", selectedLocation, "");
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Deleted",
        message: `Location "${selectedLocation.locationName}" deleted successfully!`,
        duration: 2500, // auto-close in 2.5s
      });
      const response = await axios.get(API_BASE_URL);
      const filLocation = response.data.filter((l) => l.companyKey === companyKey);
      setLocations(filLocation);

      setShowDeleteModal(false);
      setSelectedLocation
      showMyAlert({
        type: "danger",
        title: "Error",
        message:
          "Failed to delete location: " +
          (err.response?.data?.message || err.message),
        duration: 2500,
      });
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const formatDisplayTime = (shift) => {
    const s24 = to24h(shift.startTime);
    const e24 = to24h(shift.endTime);
    return `${s24} - ${e24}`;
  };

  return (
    <Container className="py-4">
      {/* Header Card */}
      <Card className="p-3 bg-light mb-3 shadow-sm border-0">
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0 fw-semibold">Add Location</h5>
          </Col>

          <Col xs="auto">
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-success"
                onClick={exportToExcel}
                disabled={loading.page || filteredLocations.length === 0}
                size="sm"
              >
                Export Excel
              </Button>
              <Button
                variant="outline-danger"
                onClick={exportToPDF}
                disabled={loading.page || filteredLocations.length === 0}
                size="sm"
              >
                Export PDF
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Search & Add Button Card */}
      <Card className="p-3 bg-light mb-4 shadow-sm border-0">
        <Row className="align-items-end g-3">
          <Col md={8} sm={12}>
            <Form.Group>
              <Form.Label className="fw-semibold text-secondary">
                Search Locations
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search here..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  disabled={loading.page}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={clearSearch}
                    title="Clear search"
                  >
                    <FaTimes />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>

          <Col
            md={4}
            sm={12}
            className="d-flex justify-content-md-end justify-content-start"
          >
            {canAdd && (
              <Button
                variant="primary"
                onClick={() => setShowMapModal(true)}
                disabled={loading.page}
                className="mt-2 mt-md-0"
              >
                <FaPlus className="me-2" />
                Add Location
              </Button>
            )}
          </Col>
        </Row>
      </Card>


      {loading.page ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">Loading locations...</p>
        </div>
      ) : (
        <>
          {searchTerm && (
            <div className="mb-3">
              <small className="text-muted">
                Showing {filteredLocations.length} of {locations.length}{" "}
                locations
                {searchTerm && ` for "${searchTerm}"`}
              </small>
            </div>
          )}

          <div className="d-none d-md-block">
            <Table striped bordered hover responsive>
              <thead>
                <tr id="text-white-important">
                  <th id="text-white-important">S.No</th>
                  <th id="text-white-important">Name</th>
                  <th id="text-white-important">Address</th>
                  <th id="text-white-important">Radius</th>
                  <th id="text-white-important">Shifts</th>
                  <th id="text-white-important">Working Hours</th>
                  {(canEdit || canDelete) && (
                    <th id="text-white-important">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-3">
                      {searchTerm
                        ? `No locations found for "${searchTerm}"`
                        : "No locations found"}
                    </td>
                  </tr>
                ) : (
                  filteredLocations.toReversed().map((location, index) => {
                    const parsedShifts = parseShifts(location.shifts);
                    return (
                      <tr key={location.id}>
                        <td>{filteredLocations.length - index}</td>
                        <td>{location.locationName}</td>
                        <td className="text-wrap" style={{ maxWidth: "250px" }}>
                          {location.address || "No address"}
                        </td>
                        <td>{location.radius}m</td>
                        <td>
                          {parsedShifts.length > 0 ? (
                            parsedShifts.map((shift, i) => (
                              <div key={i}>
                                <FaClock className="me-1 text-muted" />
                                {shift.shiftName}: {formatDisplayTime(shift)}
                              </div>
                            ))
                          ) : (
                            <span className="text-muted small">No shifts</span>
                          )}
                        </td>
                        <td>
                          {parsedShifts.length > 0 ? (
                            parsedShifts.map((shift, i) => (
                              <div key={i} className="small">
                                {shift.shiftName}: {shift.workingHours} hours
                              </div>
                            ))
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        {(canEdit || canDelete) && (
                          <td>
                            {canEdit && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(location)}
                              >
                                Edit
                              </Button>
                            )}

                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedLocation(location);
                                  setShowDeleteModal(true);
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="d-md-none">
            {filteredLocations.length === 0 ? (
              <p className="text-center text-muted">
                {searchTerm
                  ? `No locations found for "${searchTerm}"`
                  : "No locations found"}
              </p>
            ) : (
              filteredLocations.toReversed().map((location, index) => {
                const parsedShifts = parseShifts(location.shifts);
                return (
                  <div
                    key={location.id}
                    className="border rounded mb-3 shadow-sm p-3 bg-white"
                  >
                    <div className="table-responsive">
                      <Table striped hover bordered size="sm" className="mb-2">
                        <tbody>
                          <tr>
                            <td className="fw-bold">SI</td>
                            <td>{filteredLocations.length - index}</td>
                          </tr>
                          <tr>
                            <th className="fw-bold">Location Name</th>
                            <td className="fw-semibold text-dark">
                              {location.locationName}
                            </td>
                          </tr>
                          <tr>
                            <th className="fw-bold">Address</th>
                            <td className="text-dark">
                              {location.address || "No address"}
                            </td>
                          </tr>
                          <tr>
                            <th className="fw-bold">Radius</th>
                            <td>{location.radius}m</td>
                          </tr>
                          <tr>
                            <th className="fw-bold">Shifts</th>
                            <td>
                              {parsedShifts.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                  {parsedShifts.map((shift, i) => (
                                    <li key={i}>
                                      <FaClock className="me-1 text-secondary" />
                                      {shift.shiftName}:{" "}
                                      {formatDisplayTime(shift)}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">No shifts</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th className="fw-bold">Working Hours</th>
                            <td>
                              {parsedShifts.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                  {parsedShifts.map((shift, i) => (
                                    <li key={i}>
                                      {shift.shiftName}: {shift.workingHours} hours
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center border-top pt-2">
                      <Form.Check
                        type="checkbox"
                        label="Select"
                        className="small text-muted"
                      />
                      {canEdit && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(location)}
                        >
                          Edit Location
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      <Modal show={showMapModal} onHide={resetForm} size="xl" style={{zIndex: "99999"}}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedLocation ? "Edit Location" : "Add New Location"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>

          {loading.map ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading map...</p>
            </div>
          ) : (
            <Row>
              <Col md={6}>
                <div
                  ref={mapRef}
                  id="map"
                  style={{
                    height: "500px",
                    marginBottom: "20px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                ></div>
                <div className="text-muted small">
                  <strong>Tip:</strong> Click anywhere on the map to set
                  coordinates, or search for an address below.
                </div>
              </Col>
              <Col md={6}>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="locationName"
                      value={formData.locationName}
                      onChange={handleInputChange}
                      required
                      disabled={loading.map}
                      placeholder="Enter location name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Address Search</Form.Label>
                    <div className="position-relative" ref={addressInputRef}>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleAddressInputChange}
                          placeholder="Start typing address for suggestions..."
                          disabled={loading.address}
                          onFocus={() => {
                            if (addressSuggestions.length > 0) {
                              setShowSuggestions(true);
                            }
                          }}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={searchAddress}
                          disabled={loading.address || !formData.address.trim()}
                        >
                          {loading.address ? (
                            <Spinner as="span" animation="border" size="sm" />
                          ) : (
                            <FaSearch />
                          )}
                        </Button>
                      </InputGroup>

                      {/* Address Suggestions Dropdown */}
                      {showSuggestions && addressSuggestions.length > 0 && (
                        <div
                          ref={suggestionsRef}
                          className="position-absolute w-100 mt-1 border rounded shadow-sm bg-white"
                          style={{
                            zIndex: 1050,
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {loading.suggestions ? (
                            <div className="p-2 text-center">
                              <Spinner animation="border" size="sm" />
                              <span className="ms-2">
                                Loading suggestions...
                              </span>
                            </div>
                          ) : (
                            addressSuggestions.map((suggestion, index) => (
                              <div
                                key={suggestion.place_id}
                                className="p-2 border-bottom cursor-pointer hover-bg-light"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  selectAddressSuggestion(
                                    suggestion.place_id,
                                    suggestion.description
                                  )
                                }
                                onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#f8f9fa")
                                }
                                onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "white")
                                }
                              >
                                <div className="d-flex align-items-center">
                                  <FaMapMarkerAlt className="text-primary me-2" />
                                  <div>
                                    <div className="fw-bold">
                                      {
                                        suggestion.structured_formatting
                                          .main_text
                                      }
                                    </div>
                                    <div className="text-muted small">
                                      {
                                        suggestion.structured_formatting
                                          .secondary_text
                                      }
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {loading.address && (
                      <small className="text-muted">Searching address...</small>
                    )}
                    <Form.Text className="text-muted">
                      Start typing to see address suggestions, or click the
                      search button
                    </Form.Text>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Latitude</Form.Label>
                        <Form.Control
                          type="text"
                          name="latitude"
                          value={formData.latitude}
                          onChange={handleInputChange}
                          readOnly
                          placeholder="select address"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Longitude</Form.Label>
                        <Form.Control
                          type="text"
                          name="longitude"
                          value={formData.longitude}
                          onChange={handleInputChange}
                          readOnly
                          placeholder="select address"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Radius (meters) *</Form.Label>
                    <Form.Control
                      type="number"
                      name="radius"
                      min="1"
                      value={formData.radius}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter radius in meters"
                    />
                    <Form.Text className="text-muted">
                      The allowed distance range for clock-in/clock-out
                    </Form.Text>
                  </Form.Group>

                  {/* Shifts Section */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6>Shifts & Working Hours</h6>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={addShift}
                      >
                        <FaPlus className="me-1" /> Add Shift
                      </Button>
                    </div>

                    {formData.shifts.map((shift, index) => (
                      <Card key={index} className="mb-2">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Shift {index + 1}</h6>
                            {formData.shifts.length > 1 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeShift(index)}
                              >
                                <FaMinus />
                              </Button>
                            )}
                          </div>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>Shift Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={shift.shiftName}
                                  onChange={(e) =>
                                    handleShiftChange(
                                      index,
                                      "shiftName",
                                      e.target.value
                                    )
                                  }
                                  required
                                  placeholder="e.g., Morning Shift"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>Working Hours</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0.25"
                                  max="24"
                                  step="0.25"
                                  value={shift.workingHours}
                                  onChange={(e) =>
                                    handleShiftChange(
                                      index,
                                      "workingHours",
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  required
                                  placeholder="Hours"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>Start Time (24h)</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={shift.startTime}
                                  onChange={(e) =>
                                    handleShiftChange(
                                      index,
                                      "startTime",
                                      e.target.value
                                    )
                                  }
                                  required
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>End Time (auto, 24h)</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={shift.endTime}
                                  onChange={(e) =>
                                    handleShiftChange(
                                      index,
                                      "endTime",
                                      to24h(e.target.value)
                                    )
                                  }
                                  // You can keep it editable or make it readOnly if you want strict auto-calc:
                                  // readOnly
                                  required
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          {/* <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label>Break Time (minutes)</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={shift.breakTime} 
                                  onChange={(e) =>
                                    handleShiftChange(
                                      index,
                                      "breakTime",
                                      parseInt(e.target.value) || 0
                                    ) 
                                  }
                                  required
                                  placeholder="Minutes"
                                />
                              </Form.Group>
                            </Col>
                          </Row> */}

                        </Card.Body>
                      </Card>
                    ))}
                  </div>

                  <div className="d-flex justify-content-end">
                    <Button
                      variant="secondary"
                      onClick={resetForm}
                      className="me-2"
                      disabled={loading.map || loading.submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={
                        loading.map ||
                        loading.submitting ||
                        !formData.latitude ||
                        !formData.longitude ||
                        !formData.locationName
                      }
                    >
                      {loading.submitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                          <span className="ms-2">Saving...</span>
                        </>
                      ) : (
                        <>{selectedLocation ? "Update" : "Save"} Location</>
                      )}
                    </Button>
                  </div>
                </Form>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the location "
          {selectedLocation?.locationName}"?
          <br />
          <small className="text-muted">This action cannot be undone.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={loading.submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading.submitting}
          >
            {loading.submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Deleting...</span>
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddLocation;
